import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Building2, Plus, Trash2, Loader2, FolderTree } from "lucide-react";
import { toast } from "sonner";

export default function OrgUnits() {
  const { selected } = useDomains();
  const did = selected?.id;
  const [ous, setOus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", parent: "/", description: "" });

  const load = useCallback(async () => {
    if (!did) return; setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/org-units`); setOus(data); }
    catch (e) { toast.error(apiError(e)); setOus([]); } finally { setLoading(false); }
  }, [did]);
  useEffect(() => { load(); }, [load]);

  if (!selected) return <NoDomain />;
  const create = async () => { try { await api.post(`/domains/${did}/org-units`, form); toast.success("Org unit created"); setOpen(false); setForm({ name: "", parent: "/", description: "" }); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (o) => { if (!window.confirm(`Delete OU ${o.orgUnitPath}?`)) return; try { await api.delete(`/domains/${did}/org-units`, { params: { org_unit_path: o.orgUnitPath } }); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Org Units" subtitle={selected.domain} icon={Building2}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="glossy-emerald relative rounded-xl" data-testid="create-ou-button"><span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />New Org Unit</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Create org unit</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sales" data-testid="ou-name" /></div>
                <div className="space-y-1.5"><Label>Parent path</Label><Input value={form.parent} onChange={(e) => setForm({ ...form, parent: e.target.value })} placeholder="/" data-testid="ou-parent" /></div>
                <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-ou"><span className="spec-shine" />Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="glass rounded-3xl p-4">
        {loading ? <div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div> :
          ous.length === 0 ? <EmptyState label="No org units" /> :
          <div className="space-y-2">
            {ous.map((o) => (
              <div key={o.orgUnitPath} className="flex items-center justify-between rounded-2xl bg-white/60 p-4" data-testid={`ou-${o.orgUnitPath}`}>
                <div className="flex items-center gap-3">
                  <FolderTree className="h-5 w-5 text-emerald-600" />
                  <div><p className="font-medium text-slate-800">{o.name}</p><p className="font-mono text-xs text-slate-500">{o.orgUnitPath}</p></div>
                </div>
                <button onClick={() => remove(o)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>}
      </div>
    </div>
  );
}
